import { ApiClientError, requestJson, responseRecord } from './http';

export { ApiClientError as ActivationApiError };

export interface PairingGrant {
  token: string;
  expires_at: string;
  label: string;
}

export interface PairedDevice {
  device_id: string;
  label: string;
  public_key: string;
  key_algorithm: string;
  status: string;
  paired_at: string;
}

export interface SignedEntitlementDocument {
  signature: string;
  signing_key_id: string;
  [key: string]: unknown;
}

function requiredString(record: Record<string, unknown>, field: string): string {
  if (typeof record[field] !== 'string' || !record[field]) {
    throw new ApiClientError('malformed', 200, 'malformed_response', `${field} is missing from the API response`);
  }
  return record[field];
}

export async function issuePairingToken(label: string): Promise<PairingGrant> {
  const payload = await requestJson('/v1/activation/pairing-tokens', {
    method: 'POST',
    body: JSON.stringify({ label }),
  });
  const pairing = responseRecord(payload, 'pairing');
  return {
    token: requiredString(pairing, 'token'),
    expires_at: requiredString(pairing, 'expires_at'),
    label: typeof pairing.label === 'string' ? pairing.label : '',
  };
}

export async function redeemPairingToken(input: {
  token: string;
  publicKey: string;
  label?: string;
}): Promise<PairedDevice> {
  const payload = await requestJson('/v1/activation/pairing-tokens/redeem', {
    method: 'POST',
    body: JSON.stringify({
      token: input.token,
      public_key: input.publicKey,
      label: input.label || '',
      capabilities: [],
    }),
  });
  const device = responseRecord(payload, 'device');
  return {
    device_id: requiredString(device, 'device_id'),
    label: typeof device.label === 'string' ? device.label : '',
    public_key: requiredString(device, 'public_key'),
    key_algorithm: requiredString(device, 'key_algorithm'),
    status: requiredString(device, 'status'),
    paired_at: requiredString(device, 'paired_at'),
  };
}

export async function issueEntitlementDocument(): Promise<SignedEntitlementDocument> {
  const payload = await requestJson('/v1/activation/entitlement', {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return responseRecord(payload, 'entitlement_document') as SignedEntitlementDocument;
}

export interface PairingHandoff { handoff_code: string; deep_link: string; expires_in: number; }
export async function issuePairingHandoff(label: string): Promise<PairingHandoff> {
  const payload = responseRecord(await requestJson('/v1/bridge/pair-handoff', {method: 'POST', body: JSON.stringify({label})}));
  const code = requiredString(payload, 'handoff_code');
  const link = requiredString(payload, 'deep_link');
  if (!/^[A-Z2-7]{26}$/.test(code) || link !== `auteur-bridge://pair?code=${code}` || payload.expires_in !== 120) {
    throw new ApiClientError('malformed', 200, 'malformed_response', 'Pairing is temporarily unavailable.');
  }
  return {handoff_code: code, deep_link: link, expires_in: 120};
}
export async function pairingHandoffStatus(code: string): Promise<{state: string; device_id: string}> {
  const payload = responseRecord(await requestJson('/v1/bridge/pair-handoff/status', {method: 'POST', body: JSON.stringify({code})}));
  if (payload.state !== 'waiting' && payload.state !== 'paired') {
    throw new ApiClientError('malformed', 200, 'malformed_response', 'Pairing status is unavailable.');
  }
  return {state: payload.state, device_id: typeof payload.device_id === 'string' ? payload.device_id : ''};
}
